import { sendHotjarEvent } from "utils/hotjar"
import SESSION_RECORDING_EVENTS from "utils/session-recording-events"

export { SESSION_RECORDING_EVENTS }

// Hotjar stores event timestamps with millisecond precision and no sequence
// counter, so events emitted within the same tick cannot be ordered by its
// funnels. The queue below spaces emissions by EVENT_GAP_MS to keep every
// event on its own millisecond and tick.
//
// Right after surf_sub_popup_opened the Hotjar tracker may (re)establish its
// websocket. Until the socket is connected, each newly written event
// overwrites the previous unsent one, so after that event the queue holds
// back for CAPTURE_START_HOLD_MS once.
const EVENT_GAP_MS = 50
const CAPTURE_START_HOLD_MS = 2500

// The Hotjar funnel steps. Each step matches any of its alternative events.
const FUNNEL_STEPS: string[][] = [
  [SESSION_RECORDING_EVENTS.subPopupOpened],
  [
    SESSION_RECORDING_EVENTS.subPopupReading,
    SESSION_RECORDING_EVENTS.subPopupInteraction,
    SESSION_RECORDING_EVENTS.subPopupEngagementDummy,
  ],
  [SESSION_RECORDING_EVENTS.subConfirmationViewed],
  [SESSION_RECORDING_EVENTS.subCheckoutStarted],
  [SESSION_RECORDING_EVENTS.subPaymentSuccess],
]

// One representative event per step, used to backfill steps a user skipped
// (e.g. a signed-in member jumping straight to the payment screen) so the
// funnel never loses the session.
const STEP_BACKFILL_EVENTS = [
  SESSION_RECORDING_EVENTS.subPopupOpened,
  SESSION_RECORDING_EVENTS.subPopupEngagementDummy,
  SESSION_RECORDING_EVENTS.subConfirmationViewed,
  SESSION_RECORDING_EVENTS.subCheckoutStarted,
  SESSION_RECORDING_EVENTS.subPaymentSuccess,
]

const queue: string[] = []
const reachedSteps = new Set<number>()
let notBefore = 0
let holdApplied = false
let timerId: number | null = null

function funnelStepIndex(eventName: string) {
  return FUNNEL_STEPS.findIndex((names) => names.includes(eventName))
}

function processQueue() {
  timerId = null
  if (!queue.length) return

  const now = Date.now()
  if (now < notBefore) {
    timerId = window.setTimeout(processQueue, notBefore - now)
    return
  }

  const eventName = queue.shift()
  sendHotjarEvent(eventName)

  if (eventName === SESSION_RECORDING_EVENTS.subPopupOpened && !holdApplied) {
    holdApplied = true
    notBefore = now + CAPTURE_START_HOLD_MS
  } else {
    notBefore = now + EVENT_GAP_MS
  }

  if (queue.length) {
    timerId = window.setTimeout(processQueue, Math.max(notBefore - Date.now(), 0))
  }
}

function schedule(delay: number) {
  if (timerId !== null) return
  timerId = window.setTimeout(processQueue, Math.max(delay, 0))
}

export function sendSessionRecordingEvent(eventName: string) {
  const step = funnelStepIndex(eventName)
  if (step !== -1) reachedSteps.add(step)
  queue.push(eventName)
  schedule(0)
}

// Queues a funnel-step event, backfilling any earlier steps that have not
// been reached on this page, so the session always carries an ordered event
// for every step of the funnel.
export function sendSubscriptionFunnelEvent(eventName: string) {
  const step = funnelStepIndex(eventName)
  for (let i = 0; i < step; i += 1) {
    if (!reachedSteps.has(i)) sendSessionRecordingEvent(STEP_BACKFILL_EVENTS[i])
  }
  sendSessionRecordingEvent(eventName)
}
